import { useEffect, useState } from "react";

/**
 * True when the viewport is below the messaging two-pane breakpoint (~860px),
 * i.e. the mobile single-column layout is active: the messages surface shows the
 * conversation LIST or one open THREAD, never both panes at once.
 *
 * Uses `matchMedia` so the layout responds to live resizes. When `matchMedia` is
 * unavailable (SSR / jsdom under test) it defaults to FALSE — the DESKTOP
 * two-pane layout — so the list + the open thread (or the "Select a
 * conversation" placeholder) render side by side. This mirrors the documented
 * fallback of `modules/booking/hooks/useIsNarrow.ts`; the `social` module owns
 * its OWN copy because module isolation forbids importing booking's hook.
 */
const NARROW_QUERY = "(max-width: 860px)";

export function useIsNarrow(): boolean {
  const [isNarrow, setIsNarrow] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia(NARROW_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia(NARROW_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    setIsNarrow(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isNarrow;
}
